import React, {ChangeEvent} from 'react';
import {useIntl} from "react-intl";
import dayjs from "dayjs";
import {FieldArray, FormikProvider} from "formik";
import {usePriceQuoteDetailsContext} from '../../contexts/PriceQuoteDetailsContext';
import {CreatePriceQuoteGIProvider} from "../../contexts/CreatePriceQuoteGIContext";
import {PopupPriceCatalogProvider} from "../../contexts/PopupPriceCatalogContext";
import {TextSuggestionsProvider} from "../../../_common/contexts/TextSuggestionsContext";
import AnimatedUploadingFilePopUp from "../../../_common/components/AnimatedUploadingFilePopUp";
import Spinner from '../../../_common/components/Spinner';
import Unit from "../../../_common/components/Unit";
import Empty from "../../../_common/components/Empty";
import {StyledFieldInput} from "../../../_common/components/FieldInput";
import {StyledFieldSelect} from "../../../_common/components/FieldSelect";
import FieldCheckbox from "../../../_common/components/FieldCheckbox";
import PopupCreatePriceQuoteGI from "../PopupCreatePriceQuoteGI";
import ButtonUploadFile from "../../../_common/components/ButtonUploadFile";
import FileIcon from "../../../_common/components/FileIcon";
import PopupViewDocx from "../../../_common/components/PopupViewDocx/PopupViewDocx";
import ButtonViewFile from "../../../_common/components/ButtonViewFile";
import ButtonAction from "../../../_common/components/ButtonAction";
import PopupPriceCatalog from "../PopupPriceCatalog/PopupPriceCatalog";
import CommonGridHeader from "../../../_common/components/CommonGridHeader";
import {toAbsoluteUrl} from "../../../../../_metronic/_helpers";
import {priceQuoteStatus} from "../../../_common/constants/options";
import {optionsVatType} from "../../../CustomerDetails/constants/options";
import {floatValue, integerValue} from "../../../../../utils/format";
import {filterOptions} from "../../../_common/utils/options";
import {PriceQuoteFile} from "../../types";
import {createDocumentGridSelectStyles} from "../../../_common/selectStyles/customSelectStyles";
import {useCalculations} from "../CalculationHook/useCalculations";
import {usePageContext} from "../../contexts/PageContext";
import styles from "../styles.module.scss";

const File = ({file, indexToDelete}: { file: PriceQuoteFile; indexToDelete: number; }) => {
  const {
    handleDeleteFile,
    handleDownloadFile,
    handlePreviewFile,
  } = usePriceQuoteDetailsContext();
  const {name, lastModified, isGreenInvoiceFile, fileID} = file;
  const fileExtension = name ? name.substring(name.indexOf('.') + 1) : '';
  const isOfficeDocument = fileExtension === 'doc' || fileExtension === 'docx';

  return (
    <div className={styles.fileContainer}>
      <div className='d-flex'>
        <FileIcon fileExtension={fileExtension} size='sm'/>
        <div className='mx-2 d-flex flex-column justify-content-center'>
          <div className={styles.fileName}>
            <span>{name}</span>
            {isGreenInvoiceFile ? <img
              src={toAbsoluteUrl('/media/common/GI-logo.svg')}
              className={'ml-2 h-20px'} alt="i"
            /> : null}
          </div>
          {lastModified && (
            <div className={styles.fileDate}>
              {dayjs(lastModified).format('DD/MM/YYYY')}
            </div>
          )}
        </div>
      </div>
      <div className='mx-1'>
        <div className='d-flex justify-content-center'>
          {fileID && (
            <div>
              {isOfficeDocument ? (
                <PopupViewDocx
                  fileName={name}
                  onClick={() => handlePreviewFile(fileID)}
                />
              ) : (
                <ButtonViewFile
                  fileExtension={fileExtension}
                  onClick={() => handlePreviewFile(fileID)}
                />
              )}
            </div>
          )}
          {fileID && (
            <ButtonAction
              variant='icon'
              iconButtonType='secondary'
              label={<img src={toAbsoluteUrl('/media/common/Download_secondary.svg')} alt='i'/>}
              onClick={() => handleDownloadFile(fileID, name)}
              customClassnames='mx-2'
            />
          )}
          <ButtonAction
            variant='icon'
            iconButtonType='error'
            avoidDoubleClick={false}
            label={<img src={toAbsoluteUrl('/media/common/Delete_red.svg')} alt='i'/>}
            onClick={() => handleDeleteFile(fileID, indexToDelete)}
          />
        </div>
      </div>
    </div>
  )
}

const TabDetails = () => {
  const {
    loadingFile,
    formik,
    submit,
    uploadFiles,
    greenInvoiceClientID,
    customers,
    loadingData,
    isEdit,
    isPriceQuoteComplete,
  } = usePriceQuoteDetailsContext();
  const {isTab2Valid} = usePageContext();
  const {formatMessage} = useIntl();
  const prefix = 'price-quote-details';
  const {values, setFieldValue, isValid} = formik;

  const rowValuesSet = {
    vatType: optionsVatType[1],
    description: '',
    quantity: '1',
    price: '',
  };
  const {
    rows,
    totalNet,
    totalVat,
    total
  } = useCalculations(values.lines);
  const headerCells = [
    {
      showMarkNecessary: true,
      label: `מע\"מ`//vat
    },
    {
      showMarkNecessary: true,
      label: `פירוט`//desc
    },
    {
      showMarkNecessary: true,
      label: `כמות`//quantity
    },
    {
      showMarkNecessary: true,
      label: `מחיר ליח'`//price
    },
    {
      showMarkNecessary: false,
      label: 'סה\"כ לתשלום'//total
    },
    {
      showMarkNecessary: false,
      label: ''
    },
    {
      showMarkNecessary: false,
      label: ''
    },
  ];
  const headerCellsFile = [{label: `הקובץ`}];

  return (
    <FormikProvider value={formik}>
      <TextSuggestionsProvider>
        <AnimatedUploadingFilePopUp show={loadingFile}/>
        {loadingData
          ? <Spinner customContainerStyles='d-flex align-items-center justify-content-center'/>
          : (
            <>
              <div className={styles.linesFilesContainer}>
                <Unit
                  text={formatMessage({id: `${prefix}-unit-info`, defaultMessage: 'Info'})}
                  icon={'/media/common/Warning_circle_navy.svg'}
                  customClassnames={styles.detailsUnit}
                >
                  <>
                    <div>
                      <CommonGridHeader headerCells={headerCells} customStyles={styles.gridHeader}/>
                      <div className={styles.gridBody}>
                        <FieldArray name={'lines'}>
                          {({push, remove}) => {
                            return (
                              <>
                                {Array.isArray(values.lines) && values.lines.length > 0
                                  ? <div>
                                    {values.lines.map((_item, index) => (
                                      <div className={styles.gridRow}>
                                        <StyledFieldSelect
                                          fieldId={`lines.${index}.vatType`}
                                          name={`lines.${index}.vatType`}
                                          options={optionsVatType}
                                          styles={createDocumentGridSelectStyles}
                                          value={values.lines[index].vatType}
                                          onChange={({value}: any) =>
                                            setFieldValue(
                                              `lines.${index}.vatType`,
                                              filterOptions(value)(optionsVatType)[0]
                                            )
                                          }
                                        />

                                        <StyledFieldInput
                                          fieldId={`lines.${index}.description`}
                                          value={values.lines[index].description}
                                          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                                            setFieldValue(`lines.${index}.description`, e.target.value)
                                          }
                                        />

                                        <StyledFieldInput
                                          fieldId={`lines.${index}.quantity`}
                                          value={values.lines[index].quantity}
                                          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                                            setFieldValue(`lines.${index}.quantity`, integerValue(e))
                                          }
                                        />

                                        <StyledFieldInput
                                          fieldId={`lines.${index}.price`}
                                          value={values.lines[index].price}
                                          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                                            setFieldValue(`lines.${index}.price`, floatValue(e))
                                          }
                                        />

                                        <StyledFieldInput
                                          fieldId={`lines.${index}.rowAmount`}
                                          value={rows[index].total
                                            ? rows[index].total.toLocaleString('en-US', {
                                              minimumFractionDigits: 2,
                                              maximumFractionDigits: 2
                                            })
                                            : ''
                                          }
                                          disabled
                                          onChange={undefined}
                                        />

                                        <PopupPriceCatalogProvider index={index}>
                                          <PopupPriceCatalog index={index}/>
                                        </PopupPriceCatalogProvider>
                                        <ButtonAction
                                          variant={'icon'}
                                          iconButtonType={'error'}
                                          onClick={() => remove(index)}
                                          label={<img src={toAbsoluteUrl('/media/common/Delete_red.svg')}
                                                      alt="delete"/>}
                                          avoidDoubleClick={false}
                                        />
                                      </div>
                                    ))}
                                  </div>
                                  : <Empty/>
                                }
                                <ButtonAction
                                  variant="link"
                                  onClick={() => push(rowValuesSet)}
                                  label={'הוספת שורה +'}
                                  avoidDoubleClick={false}
                                  customClassnames={styles.addRowButton}
                                />
                              </>
                            )
                          }}
                        </FieldArray>
                      </div>
                    </div>
                    <div className={'d-flex align-items-center justify-content-between w-75 mx-auto'}>
                      <StyledFieldSelect
                        fieldId={'status'}
                        name={'status'}
                        options={priceQuoteStatus}
                        value={values.status}
                        customContainerStyles='w-25 mr-8'
                        onChange={({value}: any) => setFieldValue('status', filterOptions(value)(priceQuoteStatus)[0])}
                        isSearchable
                        isAsync
                        label={'סטטוס'}
                      />
                      <StyledFieldInput
                        fieldId={`totalWithoutVat`}
                        value={totalNet ? totalNet.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        }) : ''}
                        disabled
                        onChange={undefined}
                        customContainerStyles='w-25 mr-8'
                        label={'סה\"כ ₪'}
                      />
                      <StyledFieldInput
                        fieldId={`totalVat`}
                        value={totalVat ? totalVat.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        }) : ''}
                        disabled
                        onChange={undefined}
                        customContainerStyles='w-25 mr-8'
                        label={'מע\"מ ₪'}
                      />
                      <StyledFieldInput
                        fieldId={`totalWithVat`}
                        value={total ? total.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        }) : values.totalWithVat}
                        disabled
                        onChange={undefined}
                        customContainerStyles='w-25'
                        label={'סה\"כ לתשלום ₪'}
                      />
                    </div>
                  </>
                </Unit>
                <Unit
                  text={formatMessage({id: `${prefix}-unit-files`, defaultMessage: 'Files'})}
                  icon={'/media/common/Folder_green.svg'}
                >
                  <>
                    <div className='mt-n17 d-flex justify-content-end w-100'>
                      {greenInvoiceClientID && customers.length > 0 && (
                        <CreatePriceQuoteGIProvider>
                          <PopupCreatePriceQuoteGI/>
                        </CreatePriceQuoteGIProvider>
                      )}
                      <ButtonUploadFile
                        variant='link'
                        showIcon={false}
                        label={formatMessage({id: `upload-file`, defaultMessage: 'Upload file'})}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => uploadFiles(e)}
                        multiple={true}
                        customContainerStyles={'ml-8 mb-1'}
                      />
                    </div>
                    <CommonGridHeader headerCells={headerCellsFile} customStyles={styles.gridHeaderFile}/>
                    <div className={styles.filesContainer}>
                      {Array.isArray(values.files) && values.files.length > 0
                        ? values.files.map((item, index) => <File file={item} indexToDelete={index}/>)
                        : <Empty/>
                      }
                    </div>
                  </>
                </Unit>
              </div>
              <div className={'d-flex align-items-center justify-content-center mt-8'}>
                <ButtonAction
                  onClick={async () => await submit(true)}
                  disabled={!isValid || !isTab2Valid}
                  useIcon={'/media/common/Check-in-circle_white.svg'}
                  label={formatMessage({
                    id: `${prefix}-button-label-${isEdit ? 'edit' : 'create'}`,
                    defaultMessage: `${isEdit ? 'Edit' : 'Create'}`
                  })}
                  customClassnames={'mx-4 w-150px'}
                />
                <FieldCheckbox
                  fieldId='showCreateLinkModal'
                  values={values.showCreateLinkModal}
                  label={formatMessage({
                    id: `${prefix}-form-field-showCreateLinkModal`,
                    defaultMessage: 'After saving, go to the Create a link screen for confirmation'
                  })}
                  customContainerStyles={'mx-4'}
                  disabled={isPriceQuoteComplete}
                />
              </div>
            </>
          )}
      </TextSuggestionsProvider>
    </FormikProvider>
  );
};

export default TabDetails;